/**
 * Schema readers and path utilities for the schema-driven EForm.
 *
 * The submittals flow annotates schema sections/fields with `x-uioptions` to
 * control runtime behavior (auto-fill, copy-from-last, status-based locking).
 * This module turns those annotations into Sets of dot-paths that the renderer
 * and field components consume.
 */

/**
 * Returns the set of dot-separated paths that a `dynamic-select` field
 * auto-populates anywhere in the schema, via `x-uioptions.fields-to-populate`.
 *
 * `fields-to-populate` shape: { "section.field": "masterDataField", ... }
 */
export function getAutoFilledPaths(schema) {
    const result = new Set();
    if (!schema) return result;

    const walk = (node, path) => {
        if (!node || typeof node !== 'object') return;

        const props = node.properties;
        if (props && typeof props === 'object') {
            Object.entries(props).forEach(([key, child]) => {
                const childPath = path ? `${path}.${key}` : key;

                const populate = child?.['x-uioptions']?.['fields-to-populate'];
                if (populate && typeof populate === 'object') {
                    Object.keys(populate).forEach(targetPath => {
                        result.add(targetPath);
                    });
                }

                walk(child, childPath);
            });
        }

        if (node.items) {
            walk(node.items, `${path}[]`);
        }
    };

    walk(schema, '');
    return result;
}

/**
 * Walks the top-level sections of a schema and returns a Set of section keys
 * that are marked with `x-uioptions.show-copy-from-last: true`.
 */
export function getCopyFromLastSections(schema) {
    const result = new Set();
    if (!schema?.properties) return result;
    Object.entries(schema.properties).forEach(([key, def]) => {
        if (def?.['x-uioptions']?.['show-copy-from-last']) {
            result.add(key);
        }
    });
    return result;
}

/**
 * Walks `schema.properties.<a>.properties.<b>...` for a dot-separated path and
 * returns the leaf node's `title`, or `null` if any segment / title is missing.
 */
export function getSchemaTitleAtPath(schema, path) {
    if (!schema || !path) return null;
    let cur = schema;
    for (const segment of path.split('.')) {
        cur = cur?.properties?.[segment];
        if (!cur) return null;
    }
    return cur.title || null;
}

/**
 * Returns the `copy-from-last-scope` spec for a section, or `null` if absent.
 * `filterPath` is normalised to an array; `order` defaults to "DESC".
 */
export function getCopyFromLastScope(schema, sectionPath) {
    const scope = schema?.properties?.[sectionPath]?.['x-uioptions']?.['copy-from-last-scope'];
    if (!scope) return null;
    const filterPath = Array.isArray(scope.filterPath)
        ? scope.filterPath
        : (typeof scope.filterPath === 'string' && scope.filterPath ? [scope.filterPath] : []);
    if (filterPath.length === 0) return null;
    return {
        filterPath,
        orderPath: typeof scope.orderPath === 'string' ? scope.orderPath : '',
        order: scope.order === 'ASC' ? 'ASC' : 'DESC',
    };
}

/**
 * Walks the top-level sections of a schema and returns a Set of section keys
 * that are NOT editable in the given submittal status, based on each section's
 * `x-uioptions.editable-statuses` annotation.
 *
 * Sections without an `editable-statuses` annotation are always considered editable.
 */
export function getLockedPaths(schema, status) {
    const result = new Set();
    if (!schema?.properties || !status) return result;
    Object.entries(schema.properties).forEach(([key, def]) => {
        const editableStatuses = def?.['x-uioptions']?.['editable-statuses'];
        if (Array.isArray(editableStatuses) && !editableStatuses.includes(status)) {
            result.add(key);
        }
    });
    return result;
}

/**
 * Returns the set of top-level section keys that should be visually highlighted
 * as "specially editable" given the current submittal status.
 *
 * A section is highlighted when:
 *   1. It declares `x-uioptions.editable-statuses` and the current status IS in
 *      that list (the section is editable now), AND
 *   2. At least one sibling section declares `editable-statuses` that DOES NOT
 *      include the current status (i.e. some sections are locked, so visual
 *      contrast is meaningful).
 *
 * When no sibling is locked, the highlight has no contrast to convey, so the
 * function returns an empty set.
 */
export function getEditableHighlightPaths(schema, status) {
    if (!schema?.properties || !status) return new Set();
    const candidates = new Set();
    let hasLockedSection = false;
    Object.entries(schema.properties).forEach(([key, def]) => {
        const editableStatuses = def?.['x-uioptions']?.['editable-statuses'];
        if (!Array.isArray(editableStatuses)) return;
        if (editableStatuses.includes(status)) {
            candidates.add(key);
        } else {
            hasLockedSection = true;
        }
    });
    return hasLockedSection ? candidates : new Set();
}

export function parentPropsToPath(parentProps) {
    if (!Array.isArray(parentProps)) return '';
    // Replace numeric array indices with `[]` so the path matches schema-derived paths
    return parentProps
        .map(p => (typeof p === 'number' || /^\d+$/.test(String(p)) ? '[]' : p))
        .join('.')
        .split('.[]').join('[]');
}

/**
 * Returns true when `path` or any of its ancestor paths is present in `lockedPaths`.
 */
export function isPathLocked(path, lockedPaths) {
    if (!(lockedPaths instanceof Set) || !path) return false;
    if (lockedPaths.has(path)) return true;
    const parts = path.split('.');
    for (let i = 1; i < parts.length; i++) {
        if (lockedPaths.has(parts.slice(0, i).join('.'))) return true;
    }
    return false;
}
